// hooks/useColdCalendarData.js
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// ─── Mock toggle ──────────────────────────────────────────────────────────────
// Set REACT_APP_USE_MOCK_API=true in your .env to use mock data.
import realApiService from '../services/api';
import mockApiService from '../services/mockApiService';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';
const apiService = USE_MOCK ? mockApiService : realApiService;

// ─── Debounce hook ────────────────────────────────────────────────────────────
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setDebouncedValue(value), delay);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ─── Request deduplication ────────────────────────────────────────────────────
const requestCache = new Map();
const activeRequests = new Map();

const dedupedApiCall = async (endpoint, params) => {
  const { signal, ...apiParams } = params;
  const key = `${endpoint}:${JSON.stringify(apiParams)}`;

  if (activeRequests.has(key)) return activeRequests.get(key);

  if (requestCache.has(key)) {
    const cached = requestCache.get(key);
    if (Date.now() - cached.timestamp < 30000) return cached.data;
    requestCache.delete(key);
  }

  const promise = apiService.get(endpoint, apiParams, { signal });
  activeRequests.set(key, promise);

  try {
    const result = await promise;
    requestCache.set(key, { data: result, timestamp: Date.now() });
    return result;
  } finally {
    activeRequests.delete(key);
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useColdCalendarData = (filters = {}) => {
  const [data, setData] = useState({ rows: [], grand_total: null });
  const [filterOptions, setFilterOptions] = useState({
    data: {
      webinar_names: [],
      clean_test_titles: [],
      versions: [],
      available_group_by_columns: [],
      available_descriptions: [],
      available_accounts: [],
      available_emails: [],
      date_range: {},
    },
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const abortControllerRef = useRef(null);

  const stableFilters = useMemo(() => {
    const normalizeArray = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.filter((item) => item != null && item !== '');
    };

    return {
      group_by_column: filters.group_by_column || 'reporting_title',
      webinar_filter: normalizeArray(filters.webinar_filter),
      test_filter: normalizeArray(filters.test_filter),
      version_filter: normalizeArray(filters.version_filter),
      description_filter: normalizeArray(filters.description_filter),
      account_filter: normalizeArray(filters.account_filter),
      start_date: filters.start_date || '',
      end_date: filters.end_date || '',
      search_term: (filters.search_term || '').trim(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.group_by_column,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.webinar_filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.test_filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.version_filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.description_filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.account_filter),
    filters.start_date,
    filters.end_date,
    filters.search_term,
  ]);

  const debouncedFilters = useDebounce(stableFilters, 300);

  const endpoint = useMemo(() => {
    switch (debouncedFilters.group_by_column) {
      case 'reporting_description':
        return '/cold-calendar/performance-by-description';
      case 'reporting_email':
        return '/cold-calendar/performance-by-account';
      default:
        return '/cold-calendar/performance-by-title';
    }
  }, [debouncedFilters.group_by_column]);

  const requestParams = useMemo(() => {
    const params = {
      include_grand_total: 'true',
      format_display: 'true',
    };

    if (debouncedFilters.webinar_filter?.length > 0)
      params.webinar_filter = debouncedFilters.webinar_filter.join(',');
    if (debouncedFilters.start_date) params.start_date = debouncedFilters.start_date;
    if (debouncedFilters.end_date) params.end_date = debouncedFilters.end_date;
    if (debouncedFilters.search_term) params.search_term = debouncedFilters.search_term;

    switch (debouncedFilters.group_by_column) {
      case 'reporting_title':
        if (debouncedFilters.test_filter?.length > 0)
          params.test_filter = debouncedFilters.test_filter.join(',');
        if (debouncedFilters.version_filter?.length > 0)
          params.version_filter = debouncedFilters.version_filter.join(',');
        break;
      case 'reporting_description':
        if (debouncedFilters.test_filter?.length > 0)
          params.test_filter = debouncedFilters.test_filter.join(',');
        if (debouncedFilters.description_filter?.length > 0)
          params.description_filter = debouncedFilters.description_filter.join(',');
        break;
      case 'reporting_email':
        if (debouncedFilters.test_filter?.length > 0)
          params.test_filter = debouncedFilters.test_filter.join(',');
        if (debouncedFilters.account_filter?.length > 0)
          params.account_filter = debouncedFilters.account_filter.join(',');
        break;
      default:
        break;
    }

    return params;
  }, [debouncedFilters]);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const currentController = abortControllerRef.current;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const signal = currentController?.signal;

        const result = await dedupedApiCall(endpoint, {
          ...requestParams,
          ...(signal && { signal }),
        });

        if (currentController?.signal.aborted) return;

        let processedData;
        if (result?.status === 'success' && result.data) {
          processedData = {
            rows: result.data.rows || [],
            grand_total: result.data.grand_total || null,
          };
        } else if (result?.rows) {
          processedData = {
            rows: result.rows || [],
            grand_total: result.grand_total || null,
          };
        } else {
          throw new Error('Invalid API response structure');
        }

        if (!Array.isArray(processedData.rows)) {
          throw new Error('Invalid response: rows is not an array');
        }

        setData(processedData);

        if (!isRefresh) {
          try {
            const lastUpdatedResult = await dedupedApiCall('/cold-calendar/data-synced', {});
            setLastUpdated(
              lastUpdatedResult?.data?.data_last_updated || lastUpdatedResult?.data_last_updated,
            );
          } catch (timestampError) {
            console.warn('Failed to fetch timestamp:', timestampError);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;

        console.error('❌ Fetch error:', err);
        setError(err.message);
        if (!initialized) setData({ rows: [], grand_total: null });
      } finally {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
          setInitialized(true);
        }
        if (abortControllerRef.current === currentController) {
          abortControllerRef.current = null;
        }
      }
    },
    [endpoint, requestParams, initialized],
  );

  const fetchFilterOptions = useCallback(async () => {
    try {
      const groupByColumn = debouncedFilters.group_by_column;
      const params = groupByColumn ? { group_by_column: groupByColumn } : {};

      const result = await dedupedApiCall('/cold-calendar/filter-options', params);
      const dataToProcess = result?.status === 'success' ? result.data : result;

      if (dataToProcess) {
        const normalizeOptions = (options) => {
          if (!Array.isArray(options)) return [];
          return options
            .map((opt) => opt?.toString()?.trim())
            .filter((opt) => opt && opt !== 'all');
        };

        setFilterOptions({
          status: 'success',
          data: {
            available_group_by_columns: normalizeOptions(
              dataToProcess.available_group_by_columns || [],
            ),
            date_range: dataToProcess.date_range || {},
            webinar_names: normalizeOptions(dataToProcess.webinar_names || []),
            clean_test_titles: normalizeOptions(dataToProcess.clean_test_titles || []),
            versions: normalizeOptions(dataToProcess.versions || []),
            available_descriptions: normalizeOptions(dataToProcess.available_descriptions || []),
            available_accounts: normalizeOptions(dataToProcess.available_accounts || []),
            available_emails: normalizeOptions(dataToProcess.available_emails || []),
          },
        });
      }
    } catch (err) {
      console.error('❌ Error fetching filter options:', err);
      setFilterOptions((prevOptions) => ({ ...prevOptions, status: 'error', error: err.message }));
    }
  }, [debouncedFilters.group_by_column]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  const hasActiveFilters = useCallback(
    () =>
      !!(
        debouncedFilters.webinar_filter?.length ||
        debouncedFilters.test_filter?.length ||
        debouncedFilters.version_filter?.length ||
        debouncedFilters.description_filter?.length ||
        debouncedFilters.account_filter?.length ||
        debouncedFilters.start_date ||
        debouncedFilters.end_date ||
        debouncedFilters.search_term ||
        debouncedFilters.group_by_column !== 'reporting_title'
      ),
    [debouncedFilters],
  );

  const getCurrentGroupByColumn = useCallback(
    () => debouncedFilters.group_by_column,
    [debouncedFilters.group_by_column],
  );

  const supportsParsingFields = useCallback(
    (groupByColumn) => !groupByColumn || groupByColumn === 'reporting_title',
    [],
  );

  // Effects
  useEffect(() => {
    fetchFilterOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialized || !loading) fetchData();
  }, [debouncedFilters, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialized) fetchFilterOptions();
  }, [debouncedFilters.group_by_column, fetchFilterOptions]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const metadata = useMemo(
    () => ({
      filteringMode: 'server_side_with_optimized_debouncing',
      totalRecords: data.rows?.length || 0,
      hasGrandTotal: !!data.grand_total,
      usingMock: USE_MOCK,
      appliedFilters: {
        group_by: debouncedFilters.group_by_column !== 'reporting_title',
        webinar: !!debouncedFilters.webinar_filter?.length,
        test: !!debouncedFilters.test_filter?.length,
        version: !!debouncedFilters.version_filter?.length,
        description: !!debouncedFilters.description_filter?.length,
        account: !!debouncedFilters.account_filter?.length,
        dates: !!(debouncedFilters.start_date || debouncedFilters.end_date),
        search: !!debouncedFilters.search_term,
      },
      performance: {
        requestDeduplication: true,
        filterDebouncing: 300,
        cacheEnabled: true,
        abortOnNewRequest: true,
      },
      endpointUsed: endpoint,
    }),
    [data, debouncedFilters, endpoint],
  );

  return {
    data,
    filterOptions,
    loading,
    refreshing,
    error,
    initialized,
    refetch,
    lastUpdated,
    hasActiveFilters: hasActiveFilters(),
    getCurrentGroupByColumn,
    supportsParsingFields: supportsParsingFields(getCurrentGroupByColumn()),
    metadata,
  };
};

// Convenience hooks — unchanged API
export const useColdCalendarDataByType = (groupByColumn = 'reporting_title', filters = {}) => {
  const enhancedFilters = useMemo(
    () => ({ ...filters, group_by_column: groupByColumn }),
    [filters, groupByColumn],
  );
  return useColdCalendarData(enhancedFilters);
};

export const useColdCalendarDataByTitle = (filters = {}) =>
  useColdCalendarDataByType('reporting_title', filters);

export const useColdCalendarDataByDescription = (filters = {}) =>
  useColdCalendarDataByType('reporting_description', filters);

export const useColdCalendarDataByAccount = (filters = {}) =>
  useColdCalendarDataByType('reporting_email', filters);